import { useCallback, useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { ErreurApi } from '../../../api/client.js';
import { CIBLE_MIN, COULEURS, styleBouton, styleBoutonPrincipal } from '../../../app/styles.js';
import type { ActeNotarie, DonneesActes, NouvelActe, PieceGed, SourceActesNotaries, StatutActe } from './source.js';

/**
 * Onglet « Actes notariés » du dossier (PARITE-4) : disposition de la démo (dossier/ActesNotaries) branchée sur l'API réelle.
 * Actes rangés par succession, statut modifiable, pièce de la GED rattachée, retrait confirmé. Les types d'actes viennent du
 * catalogue servi par l'API ; le droit d'écrire vient des permissions de la session.
 */
export interface PropsVueActesNotaries {
  source: SourceActesNotaries;
  peutEcrire: boolean;
}

const STATUTS: readonly { code: StatutActe; libelle: string }[] = [
  { code: 'a_preparer', libelle: 'À préparer' },
  { code: 'en_cours', libelle: 'En cours' },
  { code: 'signe', libelle: 'Signé' },
  { code: 'publie', libelle: 'Publié' },
];
const libelleStatut = (s: StatutActe): string => STATUTS.find((x) => x.code === s)?.libelle ?? s;
const TONS: Readonly<Record<StatutActe, [string, string]>> = {
  a_preparer: [COULEURS.ambreFond, COULEURS.ambre],
  en_cours: ['#EEF0F8', COULEURS.marine],
  signe: ['#E4F3EC', '#1F5E43'],
  publie: ['#E4F3EC', '#1F5E43'],
};
const RAISON = 'Votre rôle ne permet pas de modifier les actes du dossier.';

const deNom = (nom: string): string => (/^[aeiouyhàâäéèêëîïôöùûüAEIOUYHÀÂÄÉÈÊËÎÏÔÖÙÛÜ]/.test(nom) ? `d’${nom}` : `de ${nom}`);
const nomActe = (a: ActeNotarie): string => `${a.kindLabel}${a.deceasedDisplayName ? `, succession ${deNom(a.deceasedDisplayName)}` : ''}`;
const dateFr = (iso: string | null): string => (iso ? iso.split('-').reverse().join('/') : 'date non précisée');
const messageDe = (e: unknown): string =>
  e instanceof ErreurApi ? e.messageUtilisateur : e instanceof Error ? e.message : 'Une erreur est survenue. Réessayez.';

const carte: CSSProperties = {
  background: COULEURS.blanc,
  border: '1px solid #E2E1EA',
  borderRadius: 12,
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  minWidth: 0,
};
const titre: CSSProperties = { margin: 0, fontSize: 18, color: COULEURS.marine };
const discret: CSSProperties = { color: COULEURS.ardoise, fontSize: 13 };
const champ: CSSProperties = {
  font: 'inherit',
  minHeight: CIBLE_MIN,
  padding: '8px 10px',
  borderRadius: 8,
  border: `1px solid ${COULEURS.bordure}`,
  width: '100%',
  boxSizing: 'border-box',
};
const grille: CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 };
const actions: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 8 };
const pastille = (s: StatutActe): CSSProperties => ({
  display: 'inline-block',
  fontSize: 12,
  fontWeight: 600,
  background: TONS[s][0],
  color: TONS[s][1],
  borderRadius: 999,
  padding: '2px 10px',
});

function Champ({ pour, libelle, children }: { pour: string; libelle: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      <label htmlFor={pour} style={{ fontWeight: 600, fontSize: 14 }}>
        {libelle}
      </label>
      {children}
    </div>
  );
}

const VIDE = { kind: '', status: 'signe' as StatutActe, successionId: '', actDate: '', etude: '', reference: '', documentId: '', note: '' };

/** Ne transmet que ce qui est saisi : un champ vide n'est pas envoyé. */
function versNouvelActe(f: typeof VIDE, typeParDefaut: string): NouvelActe {
  const e: NouvelActe = { kind: f.kind || typeParDefaut, status: f.status };
  if (f.successionId) e.successionId = f.successionId;
  if (f.actDate) e.actDate = f.actDate;
  if (f.etude.trim()) e.etude = f.etude.trim();
  if (f.reference.trim()) e.reference = f.reference.trim();
  if (f.documentId) e.documentId = f.documentId;
  if (f.note.trim()) e.note = f.note.trim();
  return e;
}

function LigneActe({
  acte,
  peutEcrire,
  enCours,
  onStatut,
  onRetirer,
}: {
  acte: ActeNotarie;
  peutEcrire: boolean;
  enCours: boolean;
  onStatut: (s: StatutActe) => void;
  onRetirer: () => void;
}) {
  const [statut, setStatut] = useState<StatutActe>(acte.status);
  const nom = nomActe(acte);
  const details = [dateFr(acte.actDate), acte.etude, acte.reference ? `Réf. ${acte.reference}` : null].filter(Boolean).join(' · ');
  return (
    <li style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: '10px 0', borderTop: '1px solid #EEEDF3' }}>
      <span style={{ flex: '1 1 240px', minWidth: 0 }}>
        <strong>{acte.kindLabel}</strong> <span style={pastille(acte.status)}>{libelleStatut(acte.status)}</span>
        <br />
        <span style={discret}>{details}</span>
        <br />
        <span style={discret}>{acte.documentTitle ? `Pièce de la GED : ${acte.documentTitle}` : 'sans pièce rattachée'}</span>
      </span>
      <select
        aria-label={`Statut : ${nom}`}
        value={statut}
        disabled={!peutEcrire || enCours}
        onChange={(e) => setStatut(e.target.value as StatutActe)}
        style={{ ...champ, width: 'auto' }}
      >
        {STATUTS.map((s) => (
          <option key={s.code} value={s.code}>
            {s.libelle}
          </option>
        ))}
      </select>
      <button
        type="button"
        style={styleBouton}
        aria-label={`Enregistrer le statut : ${nom}`}
        disabled={!peutEcrire || enCours || statut === acte.status}
        title={peutEcrire ? undefined : RAISON}
        onClick={() => onStatut(statut)}
      >
        Enregistrer le statut
      </button>
      <button
        type="button"
        style={styleBouton}
        aria-label={`Retirer : ${nom}`}
        disabled={!peutEcrire || enCours}
        title={peutEcrire ? undefined : RAISON}
        onClick={onRetirer}
      >
        Retirer
      </button>
    </li>
  );
}

export function VueActesNotaries({ source, peutEcrire }: PropsVueActesNotaries) {
  const [donnees, setDonnees] = useState<DonneesActes | null>(null);
  const [pieces, setPieces] = useState<PieceGed[]>([]);
  const [erreurLecture, setErreurLecture] = useState<string | null>(null);
  const [formulaire, setFormulaire] = useState(false);
  const [f, setF] = useState(VIDE);
  const [aRetirer, setARetirer] = useState<ActeNotarie | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const charger = useCallback(async () => {
    setErreurLecture(null);
    try {
      setDonnees(await source.lister());
    } catch (e) {
      setErreurLecture(messageDe(e));
    }
  }, [source]);

  useEffect(() => {
    void charger();
    // Les pièces ne servent qu'au rattachement : leur panne laisse l'acte enregistrable sans pièce.
    source.pieces().then(setPieces, () => setPieces([]));
  }, [charger, source]);

  async function agir(travail: () => Promise<void>): Promise<void> {
    setEnCours(true);
    setErreur(null);
    try {
      await travail();
      await charger();
    } catch (e) {
      setErreur(messageDe(e));
    } finally {
      setEnCours(false);
    }
  }

  if (erreurLecture) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
        <p role="alert" style={{ margin: 0, color: COULEURS.rouge }}>
          {erreurLecture}
        </p>
        <button type="button" style={styleBouton} onClick={() => void charger()}>
          Réessayer
        </button>
      </div>
    );
  }
  if (!donnees) return <p role="status">Chargement des actes notariés…</p>;

  const typeParDefaut = donnees.types[0]?.code ?? '';
  const groupes: { cle: string; titre: string; actes: ActeNotarie[] }[] = [];
  for (const s of donnees.successions) {
    const actes = donnees.actes.filter((a) => a.successionId === s.id);
    if (actes.length > 0) groupes.push({ cle: s.id, titre: `Succession ${deNom(s.deceasedDisplayName)}`, actes });
  }
  const connues = new Set(donnees.successions.map((s) => s.id));
  for (const a of donnees.actes.filter((x) => x.successionId !== null && !connues.has(x.successionId))) {
    const cle = a.successionId as string;
    const g = groupes.find((x) => x.cle === cle);
    if (g) g.actes.push(a);
    else groupes.push({ cle, titre: `Succession ${deNom(a.deceasedDisplayName ?? 'non précisée')}`, actes: [a] });
  }
  const sansSuccession = donnees.actes.filter((a) => a.successionId === null);
  if (sansSuccession.length > 0) groupes.push({ cle: 'dossier', titre: 'Tout le dossier', actes: sansSuccession });

  const enregistrer = () =>
    agir(async () => {
      await source.creer(versNouvelActe(f, typeParDefaut));
      setFormulaire(false);
      setF(VIDE);
    });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <p style={{ margin: 0, ...discret, fontSize: 14, flex: '1 1 320px' }}>
          Les actes que la famille ou un notaire a déjà fait établir (notoriétés, attestations, partages), et ceux à établir. Le fichier
          reste dans les documents du dossier. Réservé à l’équipe du cabinet.
        </p>
        <button
          type="button"
          style={styleBoutonPrincipal}
          disabled={!peutEcrire}
          title={peutEcrire ? undefined : RAISON}
          onClick={() => {
            setErreur(null);
            setFormulaire(true);
          }}
        >
          Enregistrer un acte
        </button>
      </div>

      {formulaire ? (
        <section aria-label="Nouvel acte notarié" style={carte}>
          <h2 style={titre}>Enregistrer un acte notarié</h2>
          <div style={grille}>
            <Champ pour="acte-type" libelle="Type d’acte">
              <select id="acte-type" value={f.kind || typeParDefaut} onChange={(e) => setF({ ...f, kind: e.target.value })} style={champ}>
                {donnees.types.map((t) => (
                  <option key={t.code} value={t.code}>
                    {t.label}
                  </option>
                ))}
              </select>
            </Champ>
            <Champ pour="acte-statut" libelle="Statut">
              <select
                id="acte-statut"
                value={f.status}
                onChange={(e) => setF({ ...f, status: e.target.value as StatutActe })}
                style={champ}
              >
                {STATUTS.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.libelle}
                  </option>
                ))}
              </select>
            </Champ>
            <Champ pour="acte-succession" libelle="Succession concernée">
              <select
                id="acte-succession"
                value={f.successionId}
                onChange={(e) => setF({ ...f, successionId: e.target.value })}
                style={champ}
              >
                <option value="">Tout le dossier</option>
                {donnees.successions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {`Succession ${deNom(s.deceasedDisplayName)}`}
                  </option>
                ))}
              </select>
            </Champ>
            <Champ pour="acte-date" libelle="Date de l’acte">
              <input id="acte-date" type="date" value={f.actDate} onChange={(e) => setF({ ...f, actDate: e.target.value })} style={champ} />
            </Champ>
            <Champ pour="acte-etude" libelle="Étude ou notaire">
              <input
                id="acte-etude"
                type="text"
                maxLength={200}
                value={f.etude}
                onChange={(e) => setF({ ...f, etude: e.target.value })}
                style={champ}
              />
            </Champ>
            <Champ pour="acte-reference" libelle="Référence de l’acte">
              <input
                id="acte-reference"
                type="text"
                maxLength={120}
                value={f.reference}
                onChange={(e) => setF({ ...f, reference: e.target.value })}
                style={champ}
              />
            </Champ>
            <Champ pour="acte-piece" libelle="Pièce de la GED (fichier de l’acte)">
              <select id="acte-piece" value={f.documentId} onChange={(e) => setF({ ...f, documentId: e.target.value })} style={champ}>
                <option value="">Aucune pièce pour l’instant</option>
                {pieces.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title}
                  </option>
                ))}
              </select>
            </Champ>
          </div>
          <Champ pour="acte-note" libelle="Note">
            <textarea
              id="acte-note"
              rows={3}
              maxLength={1000}
              value={f.note}
              onChange={(e) => setF({ ...f, note: e.target.value })}
              style={champ}
            />
          </Champ>
          <p style={{ margin: 0, ...discret }}>
            Pour joindre un fichier qui n’est pas encore dans le dossier, déposez-le d’abord dans l’onglet Documents.
          </p>
          {erreur ? (
            <p role="alert" style={{ margin: 0, color: COULEURS.rouge }}>
              {erreur}
            </p>
          ) : null}
          <div style={actions}>
            <button type="button" style={styleBouton} onClick={() => setFormulaire(false)}>
              Annuler
            </button>
            <button type="button" style={styleBoutonPrincipal} disabled={enCours || !typeParDefaut} onClick={() => void enregistrer()}>
              Enregistrer l’acte
            </button>
          </div>
        </section>
      ) : null}

      {aRetirer ? (
        <section role="alertdialog" aria-label="Retirer cet acte ?" style={{ ...carte, border: `1px solid ${COULEURS.rouge}` }}>
          <h2 style={titre}>Retirer cet acte ?</h2>
          <p style={{ margin: 0 }}>
            {`L’acte « ${nomActe(aRetirer)} » ne sera plus compté parmi les actes du dossier ; la pièce reste dans les documents. Le retrait est inscrit au journal.`}
          </p>
          {erreur ? (
            <p role="alert" style={{ margin: 0, color: COULEURS.rouge }}>
              {erreur}
            </p>
          ) : null}
          <div style={actions}>
            <button type="button" style={styleBouton} onClick={() => setARetirer(null)}>
              Annuler
            </button>
            <button
              type="button"
              style={{ ...styleBoutonPrincipal, background: COULEURS.rouge, borderColor: COULEURS.rouge }}
              disabled={enCours}
              onClick={() =>
                void agir(async () => {
                  await source.retirer(aRetirer.id);
                  setARetirer(null);
                })
              }
            >
              Retirer l’acte
            </button>
          </div>
        </section>
      ) : null}

      {!formulaire && !aRetirer && erreur ? (
        <p role="alert" style={{ margin: 0, color: COULEURS.rouge }}>
          {erreur}
        </p>
      ) : null}

      {groupes.length === 0 ? (
        <section aria-label="Actes du dossier" style={carte}>
          <p style={{ margin: 0 }}>Aucun acte notarié n’est encore enregistré sur ce dossier.</p>
          <p style={{ margin: 0, ...discret }}>
            Les notoriétés déjà obtenues par la famille se déposent ici, distinctes des pièces à réclamer.
          </p>
        </section>
      ) : (
        groupes.map((g) => (
          <section key={g.cle} aria-label={g.titre} style={carte}>
            <h2 style={titre}>{g.titre}</h2>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {g.actes.map((a) => (
                <LigneActe
                  key={`${a.id}:${a.rowVersion}`}
                  acte={a}
                  peutEcrire={peutEcrire}
                  enCours={enCours}
                  onStatut={(s) => void agir(async () => void (await source.modifier(a.id, { status: s }, a.rowVersion)))}
                  onRetirer={() => {
                    setErreur(null);
                    setARetirer(a);
                  }}
                />
              ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}
